import { useUpdateSearchParamUrl } from 'hooks/hook';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Location } from 'types/loctions';
import { SIZE_LOCATIONS_PAGE } from 'const/paging';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocations } from 'services/locations';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { isNil, omitBy } from 'lodash';
import { RoomInfo } from 'types/room';
import { isVerticalLocationLevel, renderTitle } from 'utils/renderTitleLocation';
import * as qs from 'qs';
import { StorageServices } from 'services/storage';
const storage = new StorageServices();
export const useInjection = () => {
  const { clientId, locationId, clientName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useUpdateSearchParamUrl();
  const [locations, setLocations] = useState<Location[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [searchType, setSearchType] = useState<string>('partial-text');
  const [title, setTitle] = useState<{
    title: string;
    textButton: string;
    level: number;
  }>({
    title: '',
    textButton: '',
    level: 0
  });

  const isFreeSearch = useMemo(() => {
    return !!searchParams.get('searchText');
  }, [searchParams]);

  const header = [{ label: 'Location Name' }, { label: 'Actions', width: 420 }];
  const handleGetInitLocations = useCallback(async () => {
    if (!clientId) return;
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: 1,
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      parentId: searchParams.get('searchText') ? undefined : locationId || undefined,
      clientId: clientId ? clientId : undefined,
      searchType
    };
    const data = (await getLocations(params)) as {
      locations: Location[];
      room: RoomInfo;
      total: number;
    };
    setLocations(data.locations);
    setRoomInfo(data.room);
    const levelLocationInit =
      data?.room?.find((item) => item.id === Number(locationId))?.level || 0;
    setTitle(
      renderTitle(data.locations[0]?.level ? data.locations[0]?.level : levelLocationInit + 1)
    );
    const element = document.getElementById('id-table-clients');
    if (element) {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (data.total > SIZE_LOCATIONS_PAGE) {
      setPage(2);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [clientId, locationId, searchParams, searchType]);
  const paths: RoomInfo = useMemo(() => {
    return [
      { name: 'PDF Mapping / Upload', level: 999, onClick: () => navigate('/pdfmapping') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/pdfmapping/uploadplan/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          const href = isVerticalLocationLevel(item.level)
            ? `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`
            : `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`;
          return {
            ...item,
            onClick: () => navigate(href)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);

  const fetchMoreData = useCallback(async () => {
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: page, // Use the current page
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      parentId: locationId || undefined,
      clientId: !locationId && clientId ? clientId : undefined,
      searchType
    };
    const data = await getLocations(params);
    setLocations((prevState) => [...prevState, ...data.locations]);
    if (data.total > SIZE_LOCATIONS_PAGE * page) {
      setPage((prevState) => prevState + 1);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [clientId, locationId, page, searchParams, searchType]);

  const { asyncCallback: asyncGetInitLocations } = useAsyncCallback(handleGetInitLocations, []);

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchParams(omitBy({ searchText }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  const filterByTypeAccess = useCallback(
    (type: string) => {
      setSearchParams(omitBy({ type }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  useEffect(() => {
    asyncGetInitLocations();
  }, [asyncGetInitLocations]);

  const handleBack = useCallback(() => {
    if (!locationId) {
      return navigate('/pdfmapping');
    }

    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 1) {
      pathSegments.pop();
      navigate(pathSegments.join('/'));
    }
  }, [location.pathname, locationId, navigate]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      let order = 'asc';
      const sortFieldOld = searchParams.get('sortBy');
      const orderOld = searchParams.get('order');
      if (sortFieldOld === sortField) {
        if (orderOld === 'asc') {
          order = 'desc';
        } else {
          order = 'asc';
        }
      }
      setSearchParams(
        omitBy({ sortBy: sortField, order: order }, isNil) as { [key: string]: string }
      );
    },
    [searchParams, setSearchParams]
  );
  const onCreate = (level: number) => {
    const query = qs.stringify({
      level
    });
    navigate(`${location.pathname}/upload?${query}`);
  };

  const onClickAction = useCallback(
    ({ href }: { href: string; path: string; locationId: number }) => {
      navigate(href);
    },
    [navigate]
  );

  const handleNavigateLastRoom = useCallback(() => {
    const path = storage.getLastRoomPath(clientId);
    if (path) navigate(path);
  }, [clientId, navigate]);

  const listButton = useMemo(() => {
    const buttons = [];
    if (title.level == 4) {
      buttons.push({
        textButton: 'Upload New Floor Plan',
        onClick: () => onCreate(4),
        type: 'primary'
      });
    }
    if (title.level == 5) {
      buttons.push({
        textButton: 'Upload New Room Plan',
        onClick: () => onCreate(5),
        type: 'primary'
      });
    }

    return buttons;
  }, [locations.length, onCreate, title.level, title.textButton]);

  return {
    locations,
    setLocations,
    fetchMoreData,
    hasMore,
    handleSearch,
    onSortHeader,
    filterByTypeAccess,
    searchParams,
    pathName: location.pathname,
    handleBack,
    onCreate,
    listButton,
    paths,
    onClickAction,
    clientId,
    clientName,
    title,
    header,
    isFreeSearch,
    pathLastRoom: storage.getLastRoomPath(clientId),
    handleNavigateLastRoom,
    searchType,
    setSearchType,
    asyncGetInitLocations
  };
};
